import { createTheme as createMuiTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import MuiSelect from './components/MuiSelect';
import MuiInputBase from './components/MuiInputBase';
import MuiBackdrop from './components/MuiBackdrop';
import MuiButton from './components/MuiButton';
import MuiTableCell from './components/MuiTableCell';
import MuiDivider from './components/MuiDivider';
import MuiMenuItem from './components/MuiMenuItem';
import MuiMenu from './components/MuiMenu';
import MuiSvgIcon from './components/MuiSvgIcon';
import MuiAvatar from './components/MuiAvatar';
import MuiOutlinedInput from './components/MuiOutlinedInput';
import MuiTableSortLabel from './components/MuiTableSortLabel';
import MuiFormHelperText from './components/MuiFormHelperText';
import MuiList from './components/MuiList';
import MuiListItemButton from './components/MuiListItemButton';
import MuiListItemText from './components/MuiListItemText';
import MuiInputAdornment from './components/MuiInputAdornment';
import MuiTab from './components/MuiTab';
import MuiTabs from './components/MuiTabs';
import MuiCircularProgress from './components/MuiCircularProgress';
import MuiRadio from './components/MuiRadio';
import MuiFormControlLabel from './components/MuiFormControlLabel';
import MuiCheckbox from '././components/MuiCheckbox';
import MuiTooltip from './components/MuiTooltip';
import MuiDialogTitle from './components/MuiDialogTitle';
import MuiDialogActions from './components/MuiDialogActions';
import MuiDialogContent from './components/MuiDialogContent';
import MuiPaper from './components/MuiPaper';
import MuiSwitch from './components/MuiSwitch';
import MuiAutocomplete from './components/MuiAutocomplete';
import MuiAvatarGroup from './components/MuiAvatarGroup';
import MuiCardContent from './components/MuiCardContent';
import MuiDialog from './components/MuiDialog';
import MuiPopover from './components/MuiPopover';

import './types';
import {
  defaultGreenSiteWhiteLabelConfig,
  defaultSubdomainWhiteLabelConfig,
  ThemeConfig,
} from './defaults';

export default function createTheme({
  whiteLabelConfig,
  isAdminSite,
  isSubdomain,
}: ThemeConfig) {
  const themeConfig = isAdminSite
    ? whiteLabelConfig
    : isSubdomain
    ? { ...defaultSubdomainWhiteLabelConfig, ...(whiteLabelConfig ?? {}) }
    : defaultGreenSiteWhiteLabelConfig;

  return createMuiTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    spacing: 4,
    spacingSizes: {
      xxs: 1,
      xs: 2,
      sm: 4,
      md: 5,
      lg: 6,
      xl: 8,
      xxl: 10,
      xxxl: 14,
    },
    shape: {
      borderRadius: 8,
    },
    palette: {
      mode: 'light',
      common: {
        black: '#000',
        white: '#FFF',
      },
      background: {
        default: '#F0F1EE',
        primary: themeConfig?.bgColor ?? '#F0F1EE',
        paper: '#FFFFFF',
      },
      borderColor: {
        main: grey[300],
      },
      primary: {
        main: themeConfig?.primaryColor ?? '#56B26C',
        dark: '#4BA061',
        light: '#6DC986',
        contrastText: themeConfig?.primaryContrastText ?? '#FFFFFF',
      },
      secondary: {
        main: themeConfig?.secondaryColor ?? '#2196F3',
        dark: '#1976D2',
        light: '#64B5F6',
        contrastText: '#FFFFFF',
      },
      error: {
        contrastText: '#FFFFFF',
        main: '#D32F2F',
        dark: '#C62828',
        light: '#B3261E',
      },
      warning: {
        contrastText: '#FFFFFF',
        main: '#EF6C00',
        dark: '#E65100',
      },
      info: {
        contrastText: '#FFFFFF',
        main: '#0288D1',
        dark: '#01579B',
      },
      success: {
        main: '#347B49',
        dark: '#28593A',
        light: '#56B26C',
        contrastText: '#FFFFFF',
      },
      text: {
        primary: 'rgba(0, 0, 0, 0.87)',
      },
      action: {
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        hover: 'rgba(0, 0, 0, 0.04)',
      },
    },
    fontSizes: {
      sm: 12,
      regular: 14,
      lg: 16,
      xl: 20,
      xxl: 24,
    },
    typography: (palette) => ({
      fontFamily: 'MaisonNeueNew',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: '3rem',
        fontWeight: 500,
        lineHeight: '150%',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
        lineHeight: '100%',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
        lineHeight: '150%',
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: '150%',
        color: palette.grey[900],
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
      },
      h6: {
        fontSize: '0.875rem',
        lineHeight: '150%',
        color: palette.grey[900],
      },
      body1: {
        lineHeight: '150%',
      },
      body2: {
        lineHeight: '150%',
        color: palette.grey[600],
      },
      subtitle1: {
        fontWeight: 500,
        lineHeight: '150%',
      },
      subtitle2: {
        lineHeight: '150%',
      },
      caption: {
        lineHeight: '12px',
      },
    }),
    components: {
      MuiBackdrop,
      MuiSelect,
      MuiInputBase,
      MuiButton,
      MuiTableCell,
      MuiDivider,
      MuiMenu,
      MuiMenuItem,
      MuiSvgIcon,
      MuiAvatar,
      MuiOutlinedInput,
      MuiTableSortLabel,
      MuiFormHelperText,
      MuiList,
      MuiListItemButton,
      MuiListItemText,
      MuiInputAdornment,
      MuiTab,
      MuiTabs,
      MuiCircularProgress,
      MuiRadio,
      MuiFormControlLabel,
      MuiCheckbox,
      MuiTooltip,
      MuiDialogTitle,
      MuiDialogActions,
      MuiDialogContent,
      MuiPaper,
      MuiSwitch,
      MuiAutocomplete,
      MuiAvatarGroup,
      MuiCardContent,
      MuiDialog,
      MuiPopover,
    },
  });
}

export const globalStyles = () => ({
  '*': {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSmooth: 'never',
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
  },
});
